/**
 * Overview Tab - Price health dashboard
 *
 * Shows high-level metrics, pending approvals, exceptions, and quick actions.
 * Designed to give users instant insight into pricing status.
 */

import React from 'react'
import { Card, Row, Col, Statistic, Space, Alert, Button, Table, Tag, Typography, Skeleton } from 'antd'
import {
  CheckCircleOutlined,
  WarningOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons'
import { usePriceHealth, usePriceExceptions } from '../hooks/usePriceData'
import type { PriceUpdate } from '../types'

const { Text } = Typography

interface OverviewTabProps {
  onNavigateToUpdates?: () => void
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ onNavigateToUpdates }) => {
  const { data: health, isLoading: healthLoading } = usePriceHealth()
  const { data: exceptions } = usePriceExceptions()

  if (healthLoading) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
      </Space>
    )
  }

  const exceptionCount = exceptions?.length || 0

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Exception Alert */}
      {exceptionCount > 0 && (
        <Alert
          message={
            <Space>
              <WarningOutlined />
              <Text strong>Price Review Needed</Text>
            </Space>
          }
          description={
            <div>
              <Text>
                {exceptionCount} price update{exceptionCount !== 1 ? 's' : ''} need{exceptionCount === 1 ? 's' : ''} manual review
              </Text>
              <div style={{ marginTop: 8 }}>
                {exceptions?.slice(0, 3).map((ex: PriceUpdate) => (
                  <div key={ex.uid} style={{ fontSize: '12px', color: '#666' }}>
                    • {ex.productName}: {ex.exceptionReason}
                  </div>
                ))}
              </div>
            </div>
          }
          type="warning"
          showIcon={false}
          action={
            <Button type="primary" onClick={onNavigateToUpdates}>
              Review Exceptions
            </Button>
          }
        />
      )}

      {/* Price Health Indicators */}
      <Card title={<Space><DollarOutlined />Price Health Overview</Space>}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total SKUs"
              value={health?.totalSkus || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Priced SKUs"
              value={health?.pricedSkus || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Pending Approvals"
              value={health?.pendingApprovals || 0}
              valueStyle={{ color: health?.pendingApprovals ? '#faad14' : '#52c41a' }}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Exceptions"
              value={health?.exceptions || 0}
              valueStyle={{ color: health?.exceptions ? '#ff4d4f' : '#52c41a' }}
              prefix={health?.exceptions ? <WarningOutlined /> : <CheckCircleOutlined />}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Avg Margin"
              value={health?.avgMargin || 0}
              precision={1}
              suffix="%"
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Below Min Margin"
              value={health?.belowMinMargin || 0}
              valueStyle={{ color: health?.belowMinMargin ? '#ff4d4f' : '#52c41a' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Stale Prices (90+ days)"
              value={health?.stalePrices || 0}
              valueStyle={{ color: health?.stalePrices ? '#faad14' : '#52c41a' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Avg Price Change (30d)"
              value={health?.trends?.avgPriceChange || 0}
              precision={1}
              suffix="%"
              valueStyle={{
                color: (health?.trends?.avgPriceChange || 0) > 0 ? '#52c41a' : '#ff4d4f'
              }}
              prefix={
                (health?.trends?.avgPriceChange || 0) > 0 ? <RiseOutlined /> : <FallOutlined />
              }
            />
          </Col>
        </Row>
      </Card>

      {/* Price Trends */}
      <Card title="Recent Price Trends (30 days)">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card size="small" title="Top Price Increases">
              {health?.trends?.topRisers && health.trends.topRisers.length > 0 ? (
                <Table
                  size="small"
                  dataSource={health.trends.topRisers.map((item, idx) => ({ ...item, key: idx }))}
                  columns={[
                    { title: 'SKU', dataIndex: 'productId', width: '40%' },
                    { title: 'Product', dataIndex: 'productName', ellipsis: true },
                    {
                      title: 'Change',
                      dataIndex: 'change',
                      width: '25%',
                      render: (val: number) => (
                        <Tag color="green">+{val.toFixed(1)}%</Tag>
                      ),
                    },
                  ]}
                  pagination={false}
                />
              ) : (
                <Text type="secondary">No price increases in the last 30 days</Text>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title="Top Price Decreases">
              {health?.trends?.topFallers && health.trends.topFallers.length > 0 ? (
                <Table
                  size="small"
                  dataSource={health.trends.topFallers.map((item, idx) => ({ ...item, key: idx }))}
                  columns={[
                    { title: 'SKU', dataIndex: 'productId', width: '40%' },
                    { title: 'Product', dataIndex: 'productName', ellipsis: true },
                    {
                      title: 'Change',
                      dataIndex: 'change',
                      width: '25%',
                      render: (val: number) => (
                        <Tag color="red">{val.toFixed(1)}%</Tag>
                      ),
                    },
                  ]}
                  pagination={false}
                />
              ) : (
                <Text type="secondary">No price decreases in the last 30 days</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Quick Actions */}
      <Card title="Quick Actions">
        <Space size="middle" wrap>
          <Button type="primary" onClick={onNavigateToUpdates}>
            Review Pending Updates
          </Button>
          <Button>View All Prices</Button>
          <Button>Export Price List</Button>
          <Button>Price History</Button>
        </Space>
      </Card>
    </Space>
  )
}
